import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import { Link } from 'react-router-dom';
import { SEOHead, organizationSchema, mobileAppSchema, websiteSchema, BRAND } from './SEOUtils';

export default function BlackPolyDatingPage() {
  const schemas = [organizationSchema, mobileAppSchema, websiteSchema];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Black Poly Dating App for Conscious Relationships | UnityLove"
        description="UnityLove is Black poly-centered—built for polyamory and polygamy—and welcoming to intentional monogamy. Find conscious Black connection."
        canonical="/black-poly-dating"
        schemas={schemas}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Black Poly Dating Made for Intentional Relationships
          </h1>
          <p className="text-xl text-slate-300 mb-4 max-w-2xl mx-auto">
            UnityLove is Black poly-centered—built for polyamory and polygamy—and welcoming to intentional monogamy rooted in honesty, consent, and commitment.
          </p>
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            Whether you're building a polycule, creating a polygamous family, or seeking traditional commitment within a community that respects all paths, you belong here.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/auth?mode=signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-colors"
            >
              Join UnityLove Free
            </Link>
            <a
              href={BRAND.appStoreUrl}
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-colors"
            >
              Download the App
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-16">
        
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Why Black Poly Dating Deserves Its Own Space
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            For too long, Black individuals exploring polyamory, ethical non-monogamy, and non-traditional relationship structures have been underserved by mainstream dating apps. UnityLove changes that. We built a Black poly dating platform that understands the unique cultural dynamics, values, and experiences of Black polyamorous communities.
          </p>
          <p className="text-lg text-slate-700 mb-4">
            Whether you identify as polyamorous, practice ethical non-monogamy, or are simply curious about expanding your relationship horizons, UnityLove provides a judgment-free space where Black singles and couples can connect authentically.
          </p>
          <p className="text-lg text-slate-700">
            Our platform is built on respect, consent, and intentionality—values that are central to healthy polyamorous relationships and deeply rooted in Afrocentric relationship traditions.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            What Makes UnityLove Different from Other Poly Dating Apps
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            Most poly dating apps treat polyamory as an afterthought or a niche filter. UnityLove was built from the ground up for Black polyamorous dating. Our matching algorithm considers relationship style preferences, communication styles, and what you're actually looking for—whether that's a new partner for your existing polycule, a casual connection, or a deep emotional bond.
          </p>
          <div className="grid md:grid-cols-2 gap-6 my-8">
            <div className="bg-slate-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Designed for ENM</h3>
              <p className="text-slate-700">Profile options that reflect the reality of ethical non-monogamy—list your relationship structure, partners, and what you're seeking without judgment.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Black-Centered Community</h3>
              <p className="text-slate-700">Connect with people who share your cultural background and understand the unique experience of being Black and poly in today's world.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Consent-First Culture</h3>
              <p className="text-slate-700">Every feature is designed with consent and communication at the center—because healthy poly relationships require both.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Couples Welcome</h3>
              <p className="text-slate-700">Unlike apps that ban couples, UnityLove has dedicated features for partners seeking together—ethically and transparently.</p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Black Ethical Non-Monogamy: Reclaiming Our Narrative
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            Ethical non-monogamy in Black communities isn't new—it's been practiced in various forms across African cultures for centuries. What's new is having a dedicated space to explore these relationship styles without the judgment that often comes from both mainstream society and within our own communities.
          </p>
          <p className="text-lg text-slate-700 mb-4">
            UnityLove is more than a Black poly dating app. It's a movement toward relationship liberation—where Black individuals can define love on their own terms, free from Eurocentric relationship norms that don't serve us.
          </p>
          <p className="text-lg text-slate-700">
            Whether you're exploring <Link to="/black-polyamory-dating-app" className="text-rose-600 hover:text-rose-700 underline">Black polyamory dating</Link> for the first time or you're an experienced practitioner of ethical non-monogamy, you'll find your people here.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            How to Get Started with Black Poly Dating on UnityLove
          </h2>
          <ol className="list-decimal list-inside space-y-4 text-lg text-slate-700">
            <li><strong>Create your profile:</strong> Tell us about yourself, your relationship style, and what you're looking for. Be as detailed as you want—authenticity attracts authenticity.</li>
            <li><strong>Set your preferences:</strong> Filter by relationship structure, location, interests, and more. Looking for other polyamorous Black singles? Couples seeking a third? It's all here.</li>
            <li><strong>Start connecting:</strong> Our matching system suggests compatible connections, or browse the community yourself. Send a thoughtful message and start building something real.</li>
            <li><strong>Join the community:</strong> Participate in discussions, virtual events, and speed dating nights designed for Black poly connection.</li>
          </ol>
        </section>

        {/* CTA Section */}
        <section className="bg-slate-900 text-white rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Find Your People?</h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Join the Black poly community and help shape a space built around real connections on UnityLove.
          </p>
          <Link
            to="/auth?mode=signup"
            className="inline-flex items-center justify-center px-8 py-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-colors"
          >
            Create Your Free Profile
          </Link>
        </section>

        {/* Choose Your Structure Section */}
        <section className="mb-12 bg-rose-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
            Choose the Relationship Structure That Fits Your Values
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Polyamory</h3>
              <p className="text-slate-600 text-sm">Multiple loving relationships practiced with honesty, consent, and open communication.</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Polygamy</h3>
              <p className="text-slate-600 text-sm">Structured multi-partner or multi-spouse commitment, often rooted in family, culture, and long-term responsibility.</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Intentional Monogamy</h3>
              <p className="text-slate-600 text-sm">Traditional one-partner commitment built on clarity, trust, and shared purpose.</p>
            </div>
          </div>
          <p className="text-center text-slate-700 mt-6">
            Whatever your structure, UnityLove prioritizes honesty, consent, and respect.
          </p>
        </section>

        {/* Related Pages */}
        <section className="mt-12 pt-8 border-t border-slate-200">
          <h3 className="text-xl font-semibold text-slate-900 mb-4">Explore More</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/black-polyamory-dating-app" className="text-rose-600 hover:text-rose-700 underline">Black Polyamory Dating</Link>
            <Link to="/black-polygamy-dating" className="text-rose-600 hover:text-rose-700 underline">Black Polygamy Dating</Link>
            <Link to="/ethical-non-monogamy-dating" className="text-rose-600 hover:text-rose-700 underline">Ethical Non-Monogamy Dating</Link>
            <Link to="/black-dating-app" className="text-rose-600 hover:text-rose-700 underline">Black Dating App</Link>
            <Link to="/about-unitylove" className="text-rose-600 hover:text-rose-700 underline">About UnityLove</Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 py-8 px-4">
        <div className="max-w-4xl mx-auto text-center text-slate-600">
          <p>© {new Date().getFullYear()} UnityLove. The Black poly dating app for conscious relationships.</p>
        </div>
      </footer>
    </div>
  );
}
